import { useEffect, useState, type ReactNode } from 'react';
import { fetchCollection, hbIf, hbStr } from '../reportTabData';
import type { GenerateState } from '../generateReport';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5f (builder B) — project_list, open_projects, timeline and
 * order_acknowledgment, ported 1:1 from the legacy tabs. All four templates
 * share the same "Management" header literal (timeline/order_acknowledgment
 * sit in the menu's Schedule group, but their group-name spans say
 * Management — the template literal wins, like usage_statistics'
 * "Purchasing"). dev3 note: NONE of these four report values has a row in
 * ksdm_master_ahu_report on dev3, so they appear in neither board's menu —
 * the panes are built faithfully from source anyway (the 5d ahu_model
 * precedent: panes work when granted).
 *
 * project_list (view/project_list.js 107 + view/project_list_item.js 48 +
 * collection/all.js 23 + 2 tmpls):
 *  - The view registers NO Dispatcher listeners at all — 'generate:report:
 *    project_list' has no handler, so a generate fires NO request and NO
 *    re-render; everything happens ONCE, at the first-generate construction
 *    (ensureReportView, action.js:197-224). The kit rightly has no
 *    REPORT_DISPATCH entry. The rest_report feeder
 *    /api/reports/project_list/<user_id> (api/rest_report/urls.py:29-31)
 *    is referenced by NO frontend code — server-side only, not ported.
 *  - LIVE construction flow (initialize :31-46): fetch MasterProjectColumn
 *    (/api/v1.0/master/v1.0.0/ahu/project_columns/, collection/all.js:6-9) →
 *    build column_arr (field_name) + column_css_arr (css_column_class)
 *    (:37-42) → render() the shell (:77-86 — header + column headings; this
 *    is ALSO where the .report-tabs-preloader clears, :80) →
 *    renderProjects('') (:44 → :48-75): fetch Project
 *    ('/api/v1.0/projects/?' — trailing '?' from url+query concat,
 *    collection/all.js:13-15) AND MasterProjectColumn a SECOND time (:52-56
 *    — the re-fetch result is never read, but a rejection kills the whole
 *    .then, rows included) → append one item view per project. Reproduced
 *    with a module-level once-store (the ensureReportView once-per-page
 *    construction): later mounts re-show the SAME stale data without
 *    refetching, exactly like the persistent legacy DOM.
 *  - Because render() is DEFERRED to the columns fetch, project_list is NOT
 *    in IMMEDIATE_RENDER_TABS — the pane stays empty until the fetch
 *    resolves, like legacy. (Known micro-approximation: the legacy clears
 *    the .report-tabs-preloader at that render moment; the page here clears
 *    it at the 4.5s onSettling timer instead — there is no POST to hang
 *    onTabRendered off, and the preloader is only armed by the empty-board ⟳
 *    path.)
 *  - The row build quirk is ported literally (:58-72): `self.project_arr` is
 *    ONE object for the whole pass — each project only overwrites the keys
 *    it has, and the item view renders synchronously at construction
 *    (project_list_item.js:26-32), so row N shows the ACCUMULATED object:
 *    keys a later project lacks carry the previous project's values, and the
 *    key order is first-seen order across all rows. The key filter is
 *    `$.inArray(key.replace('_id', ''), column_arr)` (:62) — first
 *    occurrence replace, strict indexOf.
 *  - project_list_item.tmpl: cells iterate the object with the POSITIONAL
 *    @index into column_css_arr — `class=" <css> "` (the #with block's
 *    literal spaces) when the lookup is truthy, class="" otherwise; the item
 *    view wraps them in <li class="row"><ul>…</ul></li>
 *    (project_list_item.js:19-21).
 *  - DEAD: the events hash 'click .done-btn' → searchDone (:27-29, :88-99)
 *    targets the Filter dropdown that is entirely inside an HTML comment in
 *    project_list.tmpl (the <!-- … --> block spanning the .filter
 *    dropdown-wrapper) — .done-btn/.search-box-list never render, so the
 *    query-building search path (and renderProjects with a non-empty query)
 *    is unreachable. model/all.js is an empty file. The console.logs
 *    (project_list.js:98, project_list_item.js:30) are noise, not ported.
 *
 * open_projects (view/open_projects.js 42 + 186-line tmpl):
 *  - No Dispatcher listeners; initialize → render() SYNCHRONOUSLY (:23-34,
 *    clearing the tabs preloader :30) → IMMEDIATE_RENDER_TABS; `rendered`
 *    ignored. Generate does nothing further (no handler, no POST) and the
 *    tab is not reopenable (no open:generated_report → empty board, page
 *    default).
 *  - The template is a STATIC PLACEHOLDER table: 12 hardcoded headings and
 *    three identical rows whose cells repeat the column names — ported
 *    verbatim. The commented-out Filter dropdown (open_projects.tmpl
 *    <!-- … --> block) never renders.
 *  - DEAD: collection/all.js (MasterProjectColumn + Project) is required by
 *    NO view — the view's define pulls only backbone/handlebars/tmpl
 *    (open_projects.js:7-11) — so NO request ever fires; model/all.js is
 *    empty. console.log (:32) noise.
 *
 * timeline (view/timeline.js 93 + view/timeline_item.js 42 +
 * collection/all.js 50 + model/all.js 17 + 2 tmpls):
 *  - initialize → render() SYNCHRONOUSLY (:28-31 — shell with the
 *    Scheduled/Actual headings + empty .timeline-table-content, clears the
 *    tabs preloader :36) → IMMEDIATE_RENDER_TABS; `rendered` ignored.
 *  - LIVE generate: 'generate:report:timeline' → renderTimelineCollection
 *    (:30, :43-85) runs on EVERY generate: empty the table (:47), then
 *    Promise.all of THREE fetches (:53-55) — Users
 *    (/api/v1.0/mfg/1/users), Timeline
 *    (/api/v1.0/projects/<project_id>/timeline/ — project_id from the
 *    dispatch state, collection/all.js:14-18) and CmsWorkflowTasksCollection
 *    (/api/v1.0/cms/1/v1.0.0/workflow/tasks/, cms_id hardcoded 1, :50) — on
 *    success build one row per WORKFLOW TASK in task order: the timeline row
 *    whose task_id === task.id (STRICT, :67) or a client-side stub
 *    {project_id, task_id} (:68-72 — Backbone collection.add, no request).
 *    Any fetch rejection → the .then never runs → the table stays empty (no
 *    error path). Reproduced per dispatch state in the pane (the AhuModelTab
 *    pattern); a re-generate re-fetches and re-renders like legacy.
 *  - The users_arranged map (:57-60, built from the RAW users fetch payload)
 *    and tasks_arranged are passed to every item view, but
 *    timeline_item.tmpl reads ONLY tasks_arranged (the task-name lookup);
 *    the by-columns render model.sch_started_by/sch_completion_by/
 *    started_by/completion_by directly. The users FETCH is still live
 *    (it gates Promise.all), so it fires here too — result unused.
 *  - timeline_item.js has NO tagName → Backbone's default div wraps the
 *    template's <li>, so each row is <div><li>…</li></div> inside the
 *    content <ul> — the invalid-nesting quirk is ported as-is.
 *  - NOT reopenable: no open:generated_report listener (:28-31) → empty
 *    board on re-open (page default).
 *  - DEAD: TasksStatusesCollection is constructed (:51) but never fetched —
 *    Backbone construction is network-free, NO request; model/all.js
 *    (TimelineModel → /ahu/filters/performances) is required by no view;
 *    the commented-out `this.renderTimelineCollection(86)` (:39) and the
 *    console.log (:75) are noise.
 *
 * order_acknowledgment (view/order_acknowledgment.js 41 + 129-line tmpl):
 *  - No Dispatcher listeners; initialize → render() SYNCHRONOUSLY (:23-34,
 *    clears the tabs preloader :31) → IMMEDIATE_RENDER_TABS; `rendered`
 *    ignored; generate does nothing further; not reopenable.
 *  - The template is a STATIC PLACEHOLDER order sheet (Company A, William
 *    Pit, the two identical shipping rows, the "Addres line 1"/"Addres Line
 *    2" typos) — ported verbatim. console.log (:32) noise.
 */

// ---- Shared "Management" header (project_list.tmpl:1-4 =
// open_projects.tmpl:1-4 = timeline.tmpl:1-5 = order_acknowledgment.tmpl:1-6:
// tooltip ": ", ADJACENT report-name (no leading space), hardcoded 2017
// time-stamp — no live code ever updates it). ----

const ManagementTabHeader = ({ name }: { name: string }) => (
  <div className="container-header clearfix">
    <h3 className="left">
      {/* sectionName = the .current_report menu button's text, i.e. the
          report display name (open_projects.js:29-31 et al.). */}
      <span className="group-name" tooltip={`: ${name}`}>
        Management
      </span>
      <span className="report-name">{name}</span>{' '}
      <span className="time-stamp">Generated on: 2017.09.13 19:10:46</span>
    </h3>
  </div>
);

// ---- Project List ----

interface ProjectColumnRow {
  field_name?: unknown;
  css_column_class?: unknown;
  name?: unknown;
}

interface ProjectListStore {
  /** null until the initialize columns fetch resolves — the render() gate
   *  (project_list.js:34-45). */
  columns: ProjectColumnRow[] | null;
  /** Accumulated-object snapshots, one per project row (see the doc block);
   *  null until renderProjects('') resolves. */
  rows: Array<Record<string, unknown>> | null;
}

/** Module-level once-store = the single legacy view instance ensureReportView
 *  builds per page (action.js:194-207). Survives the 5s revert's unmount so a
 *  re-generate re-shows the SAME stale data without refetching, exactly like
 *  the persistent legacy #project_list DOM. */
let projectListStore: ProjectListStore = { columns: null, rows: null };
let projectListConstructed = false;
const projectListListeners = new Set<() => void>();

function notifyProjectList(): void {
  projectListListeners.forEach((l) => l());
}

function subscribeProjectList(listener: () => void): () => void {
  projectListListeners.add(listener);
  return () => {
    projectListListeners.delete(listener);
  };
}

/** The one-time construction flow (project_list.js initialize :31-46 +
 *  renderProjects('') :48-75). */
function ensureProjectListConstructed(): void {
  if (projectListConstructed) return;
  projectListConstructed = true;
  void (async () => {
    let columns: ProjectColumnRow[];
    try {
      const payload = await fetchCollection<ProjectColumnRow[]>(
        '/api/v1.0/master/v1.0.0/ahu/project_columns/'
      );
      columns = Array.isArray(payload) ? payload : [];
    } catch {
      // initialize's Promise.all .then never runs — the pane stays empty
      // (project_list.js:34-45; no error path).
      return;
    }
    projectListStore = { ...projectListStore, columns }; // render() (:43)
    notifyProjectList();
    // renderProjects('') (:44): projects + the REDUNDANT second columns fetch
    // — its result is unused (:52-57 re-reads the :37-42 arrays) but its
    // rejection kills the whole .then, rows included.
    let projects: Array<Record<string, unknown>>;
    try {
      const [projectsPayload] = await Promise.all([
        fetchCollection<Array<Record<string, unknown>>>('/api/v1.0/projects/?'),
        fetchCollection<unknown>('/api/v1.0/master/v1.0.0/ahu/project_columns/'),
      ]);
      projects = Array.isArray(projectsPayload) ? projectsPayload : [];
    } catch {
      return; // rows never render (:57)
    }
    // column_arr (:37-42) — raw field_name values, strict $.inArray compare.
    const columnArr = columns.map((c) => c.field_name);
    // self.project_arr = {} ONCE for the whole pass (:58); each item view
    // renders synchronously at construction (:66-71) — snapshot per row.
    const acc: Record<string, unknown> = {};
    const rows = projects.map((project) => {
      Object.keys(project).forEach((key) => {
        if (columnArr.indexOf(key.replace('_id', '')) !== -1) {
          acc[key] = project[key];
        }
      });
      return { ...acc };
    });
    projectListStore = { ...projectListStore, rows };
    notifyProjectList();
  })();
}

/** project_list_item.tmpl:1-3 inside the view's <li class="row"> wrapper
 *  (project_list_item.js:19-21): cells iterate the accumulated object; the
 *  class comes from the POSITIONAL @index into column_css_arr —
 *  `class=" <css> "` (the #with block's literal spaces) when truthy, empty
 *  class otherwise; {{this}} is the mustache-stringified value. */
const ProjectListRow = ({
  row,
  cssArr,
}: {
  row: Record<string, unknown>;
  cssArr: unknown[];
}) => (
  <li className="row">
    <ul>
      {Object.keys(row).map((key, index) => {
        const css = cssArr[index];
        return (
          <li key={key} className={hbIf(css) ? ` ${hbStr(css)} ` : ''}>
            {hbStr(row[key])}
          </li>
        );
      })}
    </ul>
  </li>
);

export const ProjectListTab = ({ report }: GeneratedTabPaneProps) => {
  const [store, setStore] = useState<ProjectListStore>(projectListStore);
  useEffect(() => {
    ensureProjectListConstructed();
    setStore(projectListStore); // pick up progress made while unmounted
    return subscribeProjectList(() => setStore(projectListStore));
  }, []);

  const { columns, rows } = store;
  const cssArr = (columns ?? []).map((c) => c.css_column_class);

  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {/* Empty until the columns fetch resolves — render() runs inside the
          initialize .then (project_list.js:34-45). */}
      {columns && (
        <>
          <ManagementTabHeader name={report.name} />
          {/* project_list.tmpl:78-87 (the Filter dropdown block above it is
              inside an HTML comment — never rendered). */}
          <div className="content">
            <div className="content-item table-wrapper">
              <ul className="list-heading">
                {columns.map((c, i) => (
                  // tmpl:81 — adjacent lis, class/text from the collection.
                  <li key={i} className={hbStr(c.css_column_class)}>
                    {hbStr(c.name)}
                  </li>
                ))}
              </ul>
              <ul className="list-rows" id="project_list_rows">
                {(rows ?? []).map((row, i) => (
                  <ProjectListRow key={i} row={row} cssArr={cssArr} />
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---- Open Projects ----

/** open_projects.tmpl:75-100 — the 12 hardcoded heading columns; the three
 *  placeholder rows repeat the same class/text pairs (tmpl:101-185). */
const OPEN_PROJECTS_COLUMNS: Array<[string, string]> = [
  ['xxlong', 'Project Name'],
  ['xlong', 'Job Number'],
  ['medium', 'City'],
  ['xshort', 'State'],
  ['medium', 'Country'],
  ['long', 'Market'],
  ['xlong', 'Representative'],
  ['short', 'Sell'],
  ['short', 'Price'],
  ['short', 'Cost'],
  ['medium', 'Status'],
  ['medium', 'Ship Date'],
];

const OpenProjectsCells = () => (
  <>
    {OPEN_PROJECTS_COLUMNS.map(([css, text]) => (
      // Adjacent lis (</li><li…) — no whitespace nodes between them.
      <li key={css + text} className={css}>
        {text}
      </li>
    ))}
  </>
);

/** Static placeholder pane (open_projects.tmpl:74-186); renders at mount
 *  (initialize → render, open_projects.js:23-25) — `rendered` ignored, and
 *  there is no generation-in-progress container for `faded` to touch. */
export const OpenProjectsTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <ManagementTabHeader name={report.name} />
    <div className="content">
      <div className="content-item table-wrapper">
        <ul className="list-heading">
          <OpenProjectsCells />
        </ul>
        <ul className="list-rows">
          {[0, 1, 2].map((i) => (
            <li key={i} className="row">
              <ul>
                <OpenProjectsCells />
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// ---- Timeline ----

type TimelineRow = Record<string, unknown>;

interface TimelineResult {
  /** tasks_arranged (timeline.js:61-64): workflow-task id → name. */
  tasksArranged: Record<string, unknown>;
  /** One row per workflow task, in task order (timeline.js:66-74). */
  rows: TimelineRow[];
}

/** renderTimelineCollection's fetch+arrange leg (timeline.js:43-84) for one
 *  dispatch state. */
async function fetchTimeline(state: GenerateState): Promise<TimelineResult> {
  // Promise.all (:53-55): Users + Timeline + CmsWorkflowTasks. The users
  // payload only ever fed users_arranged (:57-60), which timeline_item.tmpl
  // never reads — the request is still live (a rejection kills the render).
  // TasksStatusesCollection (:51) is constructed but NEVER fetched — no
  // request.
  const [, timelinePayload, tasksPayload] = await Promise.all([
    fetchCollection<unknown>('/api/v1.0/mfg/1/users'),
    fetchCollection<TimelineRow[]>(`/api/v1.0/projects/${state.project_id}/timeline/`),
    fetchCollection<Array<Record<string, unknown>>>('/api/v1.0/cms/1/v1.0.0/workflow/tasks/'),
  ]);
  const timelineRows = Array.isArray(timelinePayload) ? timelinePayload : [];
  const tasks = Array.isArray(tasksPayload) ? tasksPayload : [];
  const tasksArranged: Record<string, unknown> = {};
  tasks.forEach((task) => {
    tasksArranged[String(task.id)] = task.name; // :61-64
  });
  // :66-74 — the timeline row whose task_id === task.id (STRICT) or a
  // client-side stub (collection.add makes no request).
  const rows = tasks.map((task) => {
    const found = timelineRows.find((row) => row.task_id === task.id);
    return found ?? ({ project_id: state.project_id, task_id: task.id } as TimelineRow);
  });
  return { tasksArranged, rows };
}

/** timeline_item.tmpl:1-22 inside the item view's DEFAULT div wrapper
 *  (timeline_item.js has no tagName) — <div><li>…</li></div> per row, the
 *  legacy invalid-nesting quirk kept. */
const TimelineItemRow = ({
  row,
  tasksArranged,
}: {
  row: TimelineRow;
  tasksArranged: Record<string, unknown>;
}) => {
  // {{#with (lookup tasks_arranged model.task_id)}} {{this}} {{/with}} —
  // the block's literal spaces wrap the name; nothing when the lookup is
  // falsy (#with isEmpty semantics).
  const taskName = tasksArranged[String(row.task_id)];
  // {{#unless model.<date>}} empty {{/unless}} — the base class keeps its
  // trailing space either way; the block adds ' empty ' (Handlebars isEmpty:
  // ''/null/undefined falsy).
  const emptyCls = (v: unknown) => (hbIf(v) ? '' : ' empty ');
  return (
    <div>
      <li>
        <span className="label main-item task-col">
          <span>{hbIf(taskName) ? ` ${hbStr(taskName)} ` : ''}</span>
        </span>
        <div className="sched-col">
          <span className={`sched-start start-col ${emptyCls(row.sch_start_date)}`}>
            <input
              className="start-sched-input"
              type="date"
              name="sch_start_date"
              value={hbStr(row.sch_start_date)}
              required
              readOnly
            />
          </span>
          {/* The by-spans keep their template's trailing whitespace (the
              value is followed by a newline before </span>). */}
          <span className="sched-by start-by-col">{hbStr(row.sch_started_by)} </span>
          <span className={`sched-complete complete-col ${emptyCls(row.sch_end_date)}`}>
            <input
              className="complete-sched-input"
              type="date"
              name="sch_end_date"
              value={hbStr(row.sch_end_date)}
              required
              readOnly
            />
          </span>
          <span className="sched-by complete-by-col">{hbStr(row.sch_completion_by)} </span>
          <span className="sched-duration duration-col">{hbStr(row.sch_days)}</span>
        </div>
        <div className="actual-col">
          <span className={`actual-start start-col ${emptyCls(row.actual_start_date)}`}>
            <input
              className="start-actual-input"
              type="date"
              name="actual_start_date"
              value={hbStr(row.actual_start_date)}
              placeholder="---"
              required
              readOnly
            />
          </span>
          <span className="actual-by start-by-col">{hbStr(row.started_by)} </span>
          <span className={`actual-complete complete-col ${emptyCls(row.actual_end_date)}`}>
            <input
              className="complete-actual-input"
              type="date"
              name="actual_end_date"
              value={hbStr(row.actual_end_date)}
              required
              readOnly
            />
          </span>
          <span className="actual-by complete-by-col">{hbStr(row.completion_by)} </span>
          <span className="actual-duration duration-col">{hbStr(row.actual_days)}</span>
        </div>
      </li>
    </div>
  );
};

/** timeline.tmpl:6-49 — the Scheduled/Actual heading block. The task-col li
 *  is separated from sched-col by whitespace; sched-col and actual-col are
 *  ADJACENT (</li><li), as are all the inner h3s. */
const TimelineHeadingCols = ({ label }: { label: string }) => (
  <>
    <h3>{label}</h3>
    <div className="list-inline">
      <h3 className="start-col">Start</h3>
      <h3 className="start-by-col">By</h3>
      <h3 className="complete-col">Complete</h3>
      <h3 className="complete-by-col">By</h3>
      <h3 className="duration-col">Days</h3>
    </div>
  </>
);

/** Renders its shell at mount (initialize → render, timeline.js:28-31) —
 *  `rendered` ignored; every generate dispatch re-runs the fetch+render leg
 *  (renderTimelineCollection, :30,:43-85). No generation-in-progress
 *  container — `faded` has nothing to touch. */
export const TimelineTab = ({ report, states }: GeneratedTabPaneProps) => {
  const [result, setResult] = useState<TimelineResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const state of states ?? []) {
        // :47 — the table empties synchronously at the trigger, before the
        // fetches.
        if (!cancelled) setResult(null);
        try {
          // eslint-disable-next-line no-await-in-loop
          const next = await fetchTimeline(state);
          if (!cancelled) setResult(next);
        } catch {
          // Promise.all rejection: the .then never runs — the table simply
          // stays empty (timeline.js:53-56; no error path).
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [states]);

  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      <ManagementTabHeader name={report.name} />
      <div className="content">
        <div className="content-item table-wrapper">
          <ul className="timeline-table-heading list-inline" id="workflow_tasks_list">
            <li className="task-col">
              <h3>Workflow Task</h3>
            </li>{' '}
            <li className="sched-col">
              <TimelineHeadingCols label="Scheduled" />
            </li>
            <li className="actual-col">
              <TimelineHeadingCols label="Actual" />
            </li>
          </ul>
          <ul className="timeline-table-content">
            {result &&
              result.rows.map((row, i) => (
                <TimelineItemRow key={i} row={row} tasksArranged={result.tasksArranged} />
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ---- Order Acknowledgment ----

/** A label + wrapped outputs li (order_acknowledgment.tmpl:31-52 shapes). */
const OrderAckOutputsLi = ({ label, children }: { label: string; children: ReactNode }) => (
  <li>
    <span className="label">{label}</span> <span className="outputs">{children}</span>
  </li>
);

/** order_acknowledgment.tmpl:87-127 — the two shipping rows are identical. */
const OrderAckShippingRow = () => (
  <li className="row">
    <span>Equipment</span> <span>10.05.2017</span> <span>10.06.2017</span>{' '}
    <span className="outputs">
      <span>
        {/* [sic] the template's "Addres line 1" / "Addres Line 2" typos. */}
        <span>Addres line 1</span> <span>Addres Line 2</span>
      </span>{' '}
      <span>
        <span>City</span> <span>State</span> <span>Postal Code</span> <span>Country</span>
      </span>{' '}
      <span>0037845996</span> <span>George Smith</span>{' '}
      <span>
        <span>6587941</span> <span>georgesmith@aaa.ext</span>
      </span>
    </span>
  </li>
);

/** Static placeholder pane (order_acknowledgment.tmpl:7-129); renders at
 *  mount (initialize → render, order_acknowledgment.js:23-25) — `rendered`
 *  ignored, no generation-in-progress container for `faded`. */
export const OrderAcknowledgmentTab = ({ report }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <ManagementTabHeader name={report.name} />
    <div className="content">
      <div className="content-item">
        <div className="report-outputs-group-wrapper">
          {/* tmpl:10-27. */}
          <ul className="report-outputs-group">
            <li>
              <span className="label">Customer</span> <span className="output">Company A</span>
            </li>
            <li>
              <span className="label">Purchase Order No.</span>{' '}
              <span className="output">123abc</span>
            </li>
            <li>
              <span className="label">Sale Amount to Customer</span>{' '}
              <span className="unit">$</span> <span className="output">154</span>
            </li>
          </ul>
          {/* tmpl:28-73. */}
          <ul className="report-outputs-group">
            <li>
              <span className="label">Owner</span> <span className="output">William Pit</span>
            </li>
            <OrderAckOutputsLi label="General Contractor">
              <span className="output">Company A</span> <span className="output">Jack Smith</span>
            </OrderAckOutputsLi>
            <OrderAckOutputsLi label="Mechanical Contractor">
              <span>Company A</span> <span>Jack Smith</span>
            </OrderAckOutputsLi>
            <OrderAckOutputsLi label="Ordering Representative">
              <span>Company A</span> <span>Jack Smith</span>
            </OrderAckOutputsLi>
            <li>
              <span className="label">Representative</span>{' '}
              <ul className="outputs address-outputs">
                {['Representative Company 1', 'Representative Company 2', 'Representative Company 3'].map(
                  (name) => (
                    <li key={name} className="long-output">
                      <span className="output">{name}</span>
                    </li>
                  )
                )}
              </ul>
            </li>
          </ul>
        </div>
        {/* tmpl:74-128. */}
        <div className="rep-table-wrapper shipping-outputs-table">
          <ul className="table-heading">
            <li>
              <h3>Equipment</h3>
            </li>
            <li>
              <h3>Ship Date Scheduled</h3>
            </li>
            <li>
              <h3>Delivery Date Scheduled</h3>
            </li>
            <li>
              <h3>Address</h3>
            </li>
          </ul>
          <ul className="table-rows">
            <OrderAckShippingRow />
            <OrderAckShippingRow />
          </ul>
        </div>
      </div>
    </div>
  </div>
);
